import * as Notifications from "expo-notifications";
import { useEffect } from "react";
import { Platform } from "react-native";

import { useAuth } from "@/lib/auth";
import { log } from "@/logger";

import { navigateFromNotificationData } from "./navigateFromNotificationData";
import { presentAndroidForegroundPush } from "./presentAndroidForegroundPush";
import { refreshQueriesFromPush } from "./refreshQueriesFromPush";
import { registerPushDevice } from "./registerPushDevice";
import { subscribeAndroidForegroundPush } from "./subscribeAndroidForegroundPush";

// Foreground only: without this, Expo hides incoming pushes while the app is open.
// Background / killed delivery still follows the user's system notification settings.
// Android: shouldPlaySound must be true or the drop-down banner is suppressed
// (expo-notifications docs), regardless of channel importance.
Notifications.setNotificationHandler({
    handleNotification: async () => ({
        shouldShowBanner: true,
        shouldShowList: true,
        shouldPlaySound: true,
        shouldSetBadge: false,
    }),
});

function asRecord(value: unknown): Record<string, unknown> | undefined {
    if (value && typeof value === "object" && !Array.isArray(value)) {
        return value as Record<string, unknown>;
    }
    return undefined;
}

/**
 * Android: custom keys are on `content.data` (and `trigger.remoteMessage.data`).
 * iOS: `content.data` is null; keys are on the APNs `userInfo` (`trigger.payload`).
 */
function getNotificationData(
    notification: Notifications.Notification,
): Record<string, unknown> | undefined {
    const { content, trigger } = notification.request;
    const pushTrigger =
        trigger &&
        typeof trigger === "object" &&
        "type" in trigger &&
        trigger.type === "push"
            ? (trigger as Notifications.PushNotificationTrigger)
            : undefined;

    return (
        asRecord(content.data) ??
        asRecord(pushTrigger?.payload) ??
        asRecord(pushTrigger?.remoteMessage?.data)
    );
}

/**
 * Registers the push device after auth and handles notification taps (deep links).
 */
export function PushNotificationsBootstrap() {
    const { isAuthenticated, isLoading } = useAuth();

    useEffect(() => {
        if (Platform.OS === "web" || isLoading || !isAuthenticated) {
            return;
        }
        log.debug("Registering push device");
        void registerPushDevice();
    }, [isAuthenticated, isLoading]);

    useEffect(() => {
        if (Platform.OS === "web") {
            return;
        }

        /**
         * Incoming push while the app is foregrounded (expo path):
         * refresh the bell; on Android also re-present locally if needed.
         */
        const receivedSubscription =
            Notifications.addNotificationReceivedListener((notification) => {
                try {
                    const data = getNotificationData(notification);
                    refreshQueriesFromPush(data);
                    void presentAndroidForegroundPush({
                        title: notification.request.content.title,
                        body: notification.request.content.body,
                        data,
                    }).catch((error) => {
                        log.warn(
                            "Failed to present Android foreground notification",
                            error,
                        );
                    });
                } catch (error) {
                    log.warn(
                        "Failed to handle incoming notification",
                        error,
                    );
                }
            });

        /**
         * Android + RNFB: FCM may arrive here instead of the expo received listener.
         */
        let cancelled = false;
        let unsubscribeAndroid: (() => void) | undefined;
        void subscribeAndroidForegroundPush().then((unsubscribe) => {
            if (cancelled) {
                unsubscribe();
                return;
            }
            unsubscribeAndroid = unsubscribe;
        });

        /**
         * Handles notification taps (deep links) when the app is running or in the foreground.
         */
        const responseSubscription =
            Notifications.addNotificationResponseReceivedListener(
                (response) => {
                    try {
                        navigateFromNotificationData(
                            getNotificationData(response.notification),
                        );
                    } catch (error) {
                        log.warn(
                            "Failed to handle notification response",
                            error,
                        );
                    }
                },
            );

        /**
         * Handles notification taps (deep links) on app cold start.
         */
        void Notifications.getLastNotificationResponseAsync().then(
            (response) => {
                if (!response) {
                    return;
                }
                navigateFromNotificationData(
                    getNotificationData(response.notification),
                );
            },
        );

        return () => {
            cancelled = true;
            receivedSubscription.remove();
            responseSubscription.remove();
            unsubscribeAndroid?.();
        };
    }, []);

    return null;
}
